import React, { Component, ErrorInfo, ReactNode } from 'react';
// Native MUI Components
import { 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Container, 
  Box, 
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Refresh as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '100vh' 
          }}>
            <Card sx={{ width: '100%', textAlign: 'center', boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" color="error" gutterBottom>
                  🙈 Oops! Something went wrong.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  An unexpected error occurred. Please try reloading the page.
                </Typography>
                
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Accordion sx={{ mb: 3, textAlign: 'left' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Error Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography 
                        component="pre" 
                        variant="caption" 
                        sx={{ 
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-all',
                          color: 'error.main',
                          fontFamily: 'monospace'
                        }}
                      >
                        {this.state.error.stack}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
                
                <Button 
                  variant="contained" 
                  onClick={this.handleReload}
                  startIcon={<RefreshIcon />}
                >
                  Reload Page
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
